'use es6';

import dispatcher from 'dispatcher/dispatcher';
import { Map as ImmutableMap, List, Set as ImmutableSet, fromJS, Iterable } from 'immutable';
import { ASSOCIATIONS } from '../actions/ActionNamespaces';
import { OBJECT_ASSOCIATION_SUCCEEDED, OBJECT_DISASSOCIATION_SUCCEEDED, QUOTES_CREATE_SUCCEEDED } from 'crm_data/actions/ActionTypes';
import { DEAL } from 'customer-data-objects/constants/ObjectTypes';
import { defineLazyKeyStore, registerLazyKeyService } from 'crm_data/store/LazyKeyStore';
import { fetchFirstPage } from './AssociationsFirstPageAPI';
import { getDealId, getQuoteId } from 'customer-data-objects/quote/model/QuoteModel';
import { DEAL_TO_QUOTE } from 'crm_data/associations/AssociationTypes';
function isIterable(ids) {
  return Iterable.isIterable(ids) || Array.isArray(ids);
}
const idTransform = key => {
  if (ImmutableMap.isMap(key)) {
    return key;
  }
  const {
    objectType,
    objectId,
    associationType
  } = key;
  return ImmutableMap({
    objectType,
    associationType,
    objectId: String(objectId)
  });
};
registerLazyKeyService({
  idTransform,
  namespace: ASSOCIATIONS,
  fetch: fetchFirstPage
});
export default defineLazyKeyStore({
  idTransform,
  namespace: ASSOCIATIONS,
  idIsValid: key => {
    if (!key) {
      return false;
    }
    const {
      objectType,
      objectId
    } = key;
    return objectType && objectId;
  }
}).defineName('AssociationsStore').defineResponseTo(QUOTES_CREATE_SUCCEEDED, (state, newQuote) => {
  if (!newQuote) {
    return state;
  }
  const dealId = getDealId(newQuote);
  const key = idTransform({
    objectType: DEAL,
    objectId: dealId,
    associationType: DEAL_TO_QUOTE
  });
  if (!state.has(key)) {
    // if deal isn't in the store, it isn't being viewed,
    // so don't unnecessarily fetch it
    return state;
  }
  return state.updateIn([key, 'results'], (associatedQuotes = List()) => {
    return associatedQuotes.concat(getQuoteId(newQuote));
  });
}).defineResponseTo(OBJECT_ASSOCIATION_SUCCEEDED, (state, {
  objectId,
  associationsAdded,
  fromObjectType,
  toObjectType
}) => {
  let stateWithAddedAssociations = state;
  let key = idTransform({
    objectType: fromObjectType,
    objectId,
    associationType: `${fromObjectType}_TO_${toObjectType}`
  });
  if (state.has(key)) {
    stateWithAddedAssociations = state.updateIn([key, 'results'], List(), ids => {
      return ids.concat(fromJS(associationsAdded));
    });
  }

  // Update the inverse association if it exists in the state
  return associationsAdded.reduce((acc, id) => {
    key = idTransform({
      objectType: toObjectType,
      objectId: id,
      associationType: `${toObjectType}_TO_${fromObjectType}`
    });
    if (!acc.has(key)) {
      return acc;
    }
    return acc.updateIn([key, 'results'], List(), ids => {
      return ids.concat(fromJS([+objectId]));
    });
  }, stateWithAddedAssociations);
}).defineResponseTo(OBJECT_DISASSOCIATION_SUCCEEDED, (state, {
  objectId,
  associationsRemoved,
  fromObjectType,
  toObjectType
}) => {
  let stateWithRemovedAssociations = state;
  let key = idTransform({
    objectType: fromObjectType,
    objectId,
    associationType: `${fromObjectType}_TO_${toObjectType}`
  });
  const associationsRemovedIterable = isIterable(associationsRemoved) ? associationsRemoved : [associationsRemoved];
  if (state.has(key)) {
    stateWithRemovedAssociations = state.updateIn([key, 'results'], ids => ImmutableSet(ids).subtract(associationsRemovedIterable).toList());
  }

  // Update the inverse association if it exists in the state
  return associationsRemovedIterable.reduce((acc, removedAssociationId) => {
    key = idTransform({
      objectType: toObjectType,
      objectId: removedAssociationId,
      associationType: `${toObjectType}_TO_${fromObjectType}`
    });
    if (!acc.has(key)) {
      return acc;
    }
    return acc.updateIn([key, 'results'], ids => {
      return ids.filterNot(id => id === +objectId);
    });
  }, stateWithRemovedAssociations);
}).register(dispatcher);